import UserAvatar from '@/components/UserAvatar'
import Username from '@/components/Username'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  Tag01Icon,
  Tick01Icon,
  Cancel01Icon,
  Delete01Icon,
  Loading03Icon
} from '@hugeicons/core-free-icons'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useSecondaryPage } from '@/PageManager'
import { npubEncode } from '@nostr/tools/nip19'
import { canWriteAtom, useCurrentPubkey } from '@/atoms/session'
import client from '@/services/client.service'
import { now } from '@/lib/utils'
import * as kinds from '@nostr/tools/kinds'
import { invalidateFavoriteFollowSets } from '@/atoms/invalidation'
import { loadFavoriteFollowSets } from '@nostr/gadgets/lists'
import { getReplaceableCoordinateFromEvent } from '@/lib/event'
import { switchFeed } from '@/atoms/feed'
import { useState, useRef, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { ResolvedSet } from '@nostr/gadgets/sets'
import { toast } from 'sonner'
import { useAtomValue } from 'jotai'

export default function FollowSetInfo({
  followSet,
  className
}: {
  followSet: ResolvedSet<string>
  className?: string
}) {
  const { t } = useTranslation()
  const { push: pushSecondaryPage } = useSecondaryPage()
  const accountPubkey = useCurrentPubkey()
  const canWrite = useAtomValue(canWriteAtom)

  const [editing, setEditing] = useState(false)
  const [editValue, setEditValue] = useState(followSet.title)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const canEdit = !deleting && canWrite && accountPubkey === followSet.pointer.pubkey

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus()
  }, [editing])

  return (
    <div className={cn('space-y-4 mb-2', className)}>
      <div className="px-4 space-y-4">
        <div className="flex items-center gap-2 justify-between">
          <div className="flex items-center gap-2 truncate min-w-0">
            <HugeiconsIcon icon={Tag01Icon} className="w-8 h-8 shrink-0" />
            {editing ? (
              <div className="flex items-center gap-1 flex-1 min-w-0">
                <Input
                  ref={inputRef}
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSave()
                    if (e.key === 'Escape') handleCancel()
                  }}
                  className="h-9 text-lg font-semibold flex-1 min-w-0"
                  disabled={saving}
                />
                {saving ? (
                  <HugeiconsIcon icon={Loading03Icon} className="animate-spin size-4" />
                ) : (
                  <>
                    <button onClick={handleSave} className="shrink-0 p-1">
                      <HugeiconsIcon icon={Tick01Icon} className="size-5" />
                    </button>
                    <button onClick={handleCancel} className="shrink-0 p-1">
                      <HugeiconsIcon icon={Cancel01Icon} className="size-5" />
                    </button>
                  </>
                )}
              </div>
            ) : (
              <div
                className="text-2xl font-semibold truncate select-text cursor-text hover:bg-accent/50 rounded px-1 -mx-1"
                onClick={() => {
                  if (canEdit) {
                    setEditValue(followSet.title)
                    setEditing(true)
                  }
                }}
              >
                {followSet.title} ({followSet.items.length})
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {deleting ? (
              <HugeiconsIcon icon={Loading03Icon} className="animate-spin size-4" />
            ) : (
              canEdit &&
              !editing && (
                <button onClick={handleDelete} className="shrink-0 p-1 text-destructive">
                  <HugeiconsIcon icon={Delete01Icon} className="size-5" />
                </button>
              )
            )}
          </div>
        </div>

        <div className="space-y-2">
          <div className="text-sm font-semibold text-muted-foreground">{t('Profiles')}</div>
          <div className="space-y-1">
            {followSet.items.map((pk) => (
              <div
                key={pk}
                className="flex items-center gap-3 cursor-pointer hover:text-foreground"
                onClick={() => pushSecondaryPage('/' + npubEncode(pk))}
              >
                <UserAvatar user={pk} size="small" />
                <Username user={pk} className="text-muted-foreground text-sm truncate" />
              </div>
            ))}
          </div>
        </div>

        {accountPubkey !== followSet.pointer.pubkey && (
          <div className="space-y-2">
            <div className="text-sm font-semibold text-muted-foreground">{t('Author')}</div>
            <div className="flex gap-2 items-center">
              <UserAvatar user={followSet.pointer.pubkey} size="small" />
              <Username user={followSet.pointer.pubkey} className="font-semibold" />
            </div>
          </div>
        )}
      </div>
    </div>
  )

  async function handleSave() {
    const trimmed = editValue.trim()
    if (!trimmed || trimmed === followSet.title) {
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      const event = await client.publish({
        kind: kinds.Followsets,
        content: '',
        tags: [
          ['d', followSet.pointer.identifier],
          ['title', trimmed],
          ...followSet.items.map((p) => ['p', p])
        ],
        created_at: now()
      })
      if (!event) {
        console.warn('failed to publish updated follow set event')
        toast.error(t('Failed to publish updated follow set event.'))
        return
      }

      invalidateFavoriteFollowSets()
      setEditing(false)
      switchFeed({
        followSet: { ...followSet, title: trimmed, event }
      })
    } finally {
      setSaving(false)
    }
  }

  function handleCancel() {
    setEditValue(followSet.title)
    setEditing(false)
  }

  async function handleDelete() {
    if (!followSet.event) return
    if (followSet.items.length > 3 && !window.confirm(t('Delete this follow set?'))) return
    setDeleting(true)

    try {
      await client.attemptDelete(followSet.event)

      const coordinate = getReplaceableCoordinateFromEvent(followSet.event)
      const fr = await loadFavoriteFollowSets(accountPubkey!, [], true)
      const tags = fr.event
        ? fr.event.tags.filter((t) => !(t[0] === 'a' && t[1] === coordinate))
        : []

      const favEvent = await client.publish({
        kind: 10021,
        content: '',
        tags,
        created_at: now()
      })
      if (favEvent) {
        await loadFavoriteFollowSets(favEvent.pubkey, undefined, favEvent)
      }
      invalidateFavoriteFollowSets()
    } catch (err) {
      console.warn('failed to delete:', (err as Error).message)
      toast.error(t('Failed to delete: {{error}}', { error: (err as Error).message }))
    }
  }
}
